import { getSecureJsonFromLocalVault, removeSecureJsonFromLocalVault, storeSecureJsonInLocalVault } from "./localVault";

const OFFLINE_KEYS = {
  authCache: "angolabi.erp.offline.authCache",
  authAttempts: "angolabi.erp.offline.authAttempts",
  companies: "angolabi.erp.offline.companies",
  catalogPrefix: "angolabi.erp.offline.catalog",
  customerPrefix: "angolabi.erp.offline.customers",
  syncQueue: "angolabi.erp.offline.syncQueue",
};

const OFFLINE_LOGIN_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const OFFLINE_LOGIN_MAX_ATTEMPTS = 5;
const OFFLINE_LOGIN_LOCKOUT_MS = 15 * 60 * 1000;
const OFFLINE_SUBSCRIPTION_DEVICE_ID_KEY = "angolabi.erp.subscription.deviceId";
const OFFLINE_OPERATIONAL_CONTEXT_KEY = "angolabi.erp.offline.operationalContext";

const OFFLINE_STORAGE_PREFIX = "angolabi.erp.offline.";
export const OFFLINE_STORAGE_READY_EVENT = "angolabi:offline-storage-ready";
const OFFLINE_AUTH_CACHE_ALIAS = "angolabi.offline.auth-cache";

let offlineStorageReadyPromise = null;

function canUseWindow() {
  return typeof window !== "undefined";
}

function isTauriDesktop() {
  return canUseWindow() && Boolean(window.__TAURI_INTERNALS__);
}

function dispatchOfflineStorageReady() {
  if (!canUseWindow()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(OFFLINE_STORAGE_READY_EVENT));
}

async function getTauriInvoke() {
  if (!isTauriDesktop()) {
    return null;
  }

  try {
    const core = await import("@tauri-apps/api/core");
    return core?.invoke || null;
  } catch {
    return null;
  }
}

async function invokeOfflineCommand(command, payload) {
  const invoke = await getTauriInvoke();
  if (!invoke) {
    return null;
  }

  return invoke(command, payload);
}

async function writePersistedRawValue(key, rawValue) {
  const invoke = await getTauriInvoke();
  if (!invoke) {
    return;
  }

  await invoke("offline_storage_set_json", { key, value: rawValue });
}

async function removePersistedValue(key) {
  const invoke = await getTauriInvoke();
  if (!invoke) {
    return;
  }

  await invoke("offline_storage_remove", { key });
}

export async function ensureOfflineStorageReady(options = {}) {
  if (!isTauriDesktop()) {
    return false;
  }

  if (!options?.force && offlineStorageReadyPromise) {
    return offlineStorageReadyPromise;
  }

  offlineStorageReadyPromise = (async () => {
    const invoke = await getTauriInvoke();
    if (!invoke || !canUseWindow()) {
      return false;
    }

    const keys = await invoke("offline_storage_list_keys", { prefix: OFFLINE_STORAGE_PREFIX });
    const entries = await Promise.all((Array.isArray(keys) ? keys : []).map(async (key) => {
      const value = await invoke("offline_storage_get_json", { key });
      return [key, value];
    }));

    entries.forEach(([key, value]) => {
      if (!key || value === null || value === undefined) {
        return;
      }

      if (key === OFFLINE_KEYS.authCache) {
        return;
      }

      window.localStorage.setItem(key, String(value));
    });

    try {
      const legacyAuthCacheRaw = String(window.localStorage.getItem(OFFLINE_KEYS.authCache) || "").trim();
      if (legacyAuthCacheRaw) {
        await storeSecureJsonInLocalVault({
          alias: OFFLINE_AUTH_CACHE_ALIAS,
          kind: "offline-auth-cache",
          value: JSON.parse(legacyAuthCacheRaw),
          scope: "current-user",
          source: "legacy-localstorage-migration",
        });
        window.localStorage.removeItem(OFFLINE_KEYS.authCache);
        await removePersistedValue(OFFLINE_KEYS.authCache);
      }
    } catch {
      // mantém compatibilidade se a migração falhar
    }

    try {
      await invoke("local_vault_ensure_bundled_producer_key", {});
    } catch {
      // O cofre local do desktop e opcional fora do modo Tauri.
    }

    dispatchOfflineStorageReady();
    return true;
  })().catch(() => false);

  return offlineStorageReadyPromise;
}

function readJson(key, fallbackValue) {
  if (!canUseWindow()) {
    return fallbackValue;
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : fallbackValue;
  } catch {
    return fallbackValue;
  }
}

function writeJson(key, value) {
  if (!canUseWindow()) {
    return;
  }

  const rawValue = JSON.stringify(value);
  window.localStorage.setItem(key, rawValue);
  void writePersistedRawValue(key, rawValue);
}

async function readOfflineAuthCache() {
  if (isTauriDesktop()) {
    try {
      const rawValue = await getSecureJsonFromLocalVault(OFFLINE_AUTH_CACHE_ALIAS);
      if (typeof rawValue === "string" && rawValue.trim()) {
        return JSON.parse(rawValue);
      }
    } catch {
      // fallback below
    }
  }

  return readJson(OFFLINE_KEYS.authCache, {});
}

async function writeOfflineAuthCache(value) {
  const normalizedValue = value && typeof value === "object" ? value : {};

  if (isTauriDesktop()) {
    await storeSecureJsonInLocalVault({
      alias: OFFLINE_AUTH_CACHE_ALIAS,
      kind: "offline-auth-cache",
      value: normalizedValue,
      scope: "current-user",
      source: "desktop-offline-auth",
    });

    if (canUseWindow()) {
      window.localStorage.removeItem(OFFLINE_KEYS.authCache);
    }
    await removePersistedValue(OFFLINE_KEYS.authCache);
    return;
  }

  writeJson(OFFLINE_KEYS.authCache, normalizedValue);
}

async function removeOfflineAuthCache() {
  if (isTauriDesktop()) {
    await removeSecureJsonFromLocalVault(OFFLINE_AUTH_CACHE_ALIAS);
    if (canUseWindow()) {
      window.localStorage.removeItem(OFFLINE_KEYS.authCache);
    }
    await removePersistedValue(OFFLINE_KEYS.authCache);
    return;
  }

  deleteJson(OFFLINE_KEYS.authCache);
}

async function cleanupOfflineAuthCache(options = {}) {
  const currentCache = await readOfflineAuthCache();
  const currentDeviceId = readOfflineBoundDeviceId();
  const nextCache = {};
  let removed = 0;

  Object.entries(currentCache || {}).forEach(([loginKey, entry]) => {
    if (!entry || typeof entry !== "object") {
      removed += 1;
      return;
    }

    const cachedAt = Number(entry?.cachedAt || 0);
    const isExpired = !Number.isFinite(cachedAt) || cachedAt <= 0 || (Date.now() - cachedAt) > OFFLINE_LOGIN_CACHE_MAX_AGE_MS;
    const boundDeviceId = String(entry?.boundDeviceId || "").trim();
    const isWrongDevice = Boolean(boundDeviceId && currentDeviceId && boundDeviceId !== currentDeviceId);
    const hasInvalidSubscription = !isOfflineSubscriptionSnapshotValid(entry?.subscriptionSnapshot);

    const targetUserUuid = String(options?.userUuid || "").trim();
    const targetCompanyUuid = String(options?.companyUuid || "").trim();
    const entryUserUuid = String(entry?.user?.uuid || "").trim();
    const entryCompanyUuid = String(entry?.subscriptionSnapshot?.companyUuid || entry?.user?.companyUuid || "").trim();
    const shouldDropByScope = (
      (targetUserUuid && entryUserUuid && entryUserUuid === targetUserUuid)
      || (targetCompanyUuid && entryCompanyUuid && entryCompanyUuid === targetCompanyUuid)
    ) && Boolean(options?.dropScope);

    if (isExpired || isWrongDevice || hasInvalidSubscription || shouldDropByScope) {
      clearOfflineLoginAttempts(loginKey);
      removed += 1;
      return;
    }

    nextCache[loginKey] = entry;
  });

  if (removed > 0) {
    if (Object.keys(nextCache).length > 0) {
      await writeOfflineAuthCache(nextCache);
    } else {
      await removeOfflineAuthCache();
    }
  }

  return { removed, cache: removed > 0 ? nextCache : currentCache };
}
function deleteJson(key) {
  if (!canUseWindow()) {
    return;
  }

  window.localStorage.removeItem(key);
  void removePersistedValue(key);
}

export function readPersistedJson(key, fallbackValue = null) {
  return readJson(key, fallbackValue);
}

export async function writePersistedJson(key, value) {
  if (!canUseWindow()) {
    return;
  }

  const rawValue = JSON.stringify(value);
  window.localStorage.setItem(key, rawValue);
  await writePersistedRawValue(key, rawValue);
}

export async function removePersistedJson(key) {
  if (!canUseWindow()) {
    return;
  }

  window.localStorage.removeItem(key);
  await removePersistedValue(key);
}

function toHex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function hashOfflineSecret(value) {
  const normalizedValue = String(value || "");

  if (typeof window !== "undefined" && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedValue);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return toHex(digest);
  }

  return normalizedValue;
}

export function isNetworkUnavailable(error) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }

  const message = String(error?.message || "").toLowerCase();
  return !error?.response && (
    message.includes("network error")
    || message.includes("failed to fetch")
    || message.includes("load failed")
    || message.includes("networkerror")
  );
}

export function writeOfflineCompanies(companies) {
  writeJson(OFFLINE_KEYS.companies, Array.isArray(companies) ? companies : []);
}

export function readOfflineCompanies() {
  return readJson(OFFLINE_KEYS.companies, []);
}

function readOfflineBoundDeviceId() {
  if (!canUseWindow()) {
    return "";
  }

  try {
    return String(window.localStorage.getItem(OFFLINE_SUBSCRIPTION_DEVICE_ID_KEY) || "").trim();
  } catch {
    return "";
  }
}

function parseOfflineDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const normalized = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(raw)
    ? raw.replace(" ", "T")
    : raw;

  const parsed = new Date(normalized);
  const time = parsed.getTime();
  return Number.isFinite(time) ? parsed : null;
}

function resolveOfflineSubscriptionSnapshot({ user, companies }) {
  const normalizedCompanies = Array.isArray(companies) ? companies : [];
  const companyUuid = String(user?.companyUuid || user?.company_uuid || "").trim();
  const companyId = String(user?.companyId || user?.company_code || "").trim();
  const storeUuid = String(user?.storeUuid || user?.store_uuid || "").trim();
  const storeId = String(user?.storeId || user?.store_code || "").trim();

  const company = normalizedCompanies.find((item) => {
    const currentUuid = String(item?.uuid || "").trim();
    const currentId = String(item?.id || item?.code || "").trim();
    return (companyUuid && currentUuid === companyUuid) || (companyId && currentId === companyId);
  }) || null;

  const stores = Array.isArray(company?.stores) ? company.stores : [];
  const store = stores.find((item) => {
    const currentUuid = String(item?.uuid || "").trim();
    const currentId = String(item?.id || item?.code || "").trim();
    return (storeUuid && currentUuid === storeUuid) || (storeId && currentId === storeId);
  }) || null;

  const hasStoreScope = Boolean(
    store
    && (
      String(store?.subscriptionPlan || "").trim()
      || String(store?.subscriptionActivatedAt || "").trim()
      || String(store?.subscriptionExpirationAt || "").trim()
      || typeof store?.subscriptionIsActive === "boolean"
    )
  );

  const target = hasStoreScope ? store : company;
  if (!target) {
    return null;
  }

  return {
    scope: hasStoreScope ? "store" : "company",
    companyUuid: String(company?.uuid || companyUuid || "").trim() || null,
    storeUuid: hasStoreScope ? (String(store?.uuid || storeUuid || "").trim() || null) : null,
    planCode: String(target?.subscriptionPlan || "").trim() || null,
    isActive: typeof target?.subscriptionIsActive === "boolean" ? target.subscriptionIsActive : null,
    activatedAt: String(target?.subscriptionActivatedAt || "").trim() || null,
    expirationAt: String(target?.subscriptionExpirationAt || "").trim() || null,
  };
}

function isOfflineSubscriptionSnapshotValid(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return true;
  }

  if (snapshot?.isActive === false) {
    return false;
  }

  const expirationDate = parseOfflineDate(snapshot?.expirationAt);
  if (expirationDate && expirationDate.getTime() < Date.now()) {
    return false;
  }

  return true;
}

function resolveLoginCacheKey({ email, phone }) {
  const emailKey = String(email || "").trim().toLowerCase();
  if (emailKey) return emailKey;

  const phoneDigits = String(phone || "").replace(/\D+/g, "").trim();
  return phoneDigits;
}

function isTruthyActiveValue(value) {
  if (typeof value === "boolean") return value;
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  return ["1", "true", "active", "ativo", "activa", "ativo(a)", "enabled"].includes(normalized);
}

function isFalsyInactiveValue(value) {
  if (typeof value === "boolean") return !value;
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  return ["0", "false", "inactive", "inativo", "inactiva", "disabled", "blocked", "bloqueado"].includes(normalized);
}

function resolveOfflineUserIsActive(user) {
  if (!user || typeof user !== "object") {
    return false;
  }

  const inactiveSignals = [
    user?.isBlocked,
    user?.bloqueado,
    user?.blocked,
    user?.disabled,
    user?.inativo,
  ];
  if (inactiveSignals.some((value) => isTruthyActiveValue(value))) {
    return false;
  }

  const explicitStatus = [
    user?.isActive,
    user?.ativo,
    user?.active,
    user?.status,
    user?.estado,
    user?.estado_utilizador,
    user?.accountStatus,
  ];

  if (explicitStatus.some((value) => isFalsyInactiveValue(value))) {
    return false;
  }

  if (explicitStatus.some((value) => isTruthyActiveValue(value))) {
    return true;
  }

  // Se o cache não tiver flag explícita, assume ativo por retrocompatibilidade.
  return true;
}

function readOfflineAttemptsCache() {
  return readJson(OFFLINE_KEYS.authAttempts, {});
}

function writeOfflineAttemptsCache(value) {
  writeJson(OFFLINE_KEYS.authAttempts, value && typeof value === "object" ? value : {});
}

function clearOfflineLoginAttempts(loginKey) {
  if (!loginKey) return;
  const cache = readOfflineAttemptsCache();
  if (!cache?.[loginKey]) return;
  delete cache[loginKey];
  writeOfflineAttemptsCache(cache);
}

function registerOfflineLoginFailure(loginKey) {
  if (!loginKey) return;
  const now = Date.now();
  const cache = readOfflineAttemptsCache();
  const current = cache?.[loginKey] && typeof cache[loginKey] === "object" ? cache[loginKey] : {};
  const currentCount = Number(current?.count || 0);
  const nextCount = Number.isFinite(currentCount) && currentCount > 0 ? (currentCount + 1) : 1;

  const nextRecord = {
    count: nextCount,
    lastFailedAt: now,
    lockedUntil: nextCount >= OFFLINE_LOGIN_MAX_ATTEMPTS ? (now + OFFLINE_LOGIN_LOCKOUT_MS) : 0,
  };

  cache[loginKey] = nextRecord;
  writeOfflineAttemptsCache(cache);
}

function resolveOfflineLockState(loginKey) {
  if (!loginKey) {
    return { isLocked: false, remainingMs: 0 };
  }

  const cache = readOfflineAttemptsCache();
  const entry = cache?.[loginKey];
  const lockedUntil = Number(entry?.lockedUntil || 0);

  if (!Number.isFinite(lockedUntil) || lockedUntil <= 0) {
    return { isLocked: false, remainingMs: 0 };
  }

  const remainingMs = lockedUntil - Date.now();
  if (remainingMs <= 0) {
    delete cache[loginKey];
    writeOfflineAttemptsCache(cache);
    return { isLocked: false, remainingMs: 0 };
  }

  return { isLocked: true, remainingMs };
}

export async function cacheOfflineLoginRecord({ email, phone, password, user, companies, session }) {
  const loginKey = resolveLoginCacheKey({ email, phone });
  if (!loginKey || !password || !user) {
    return;
  }

  const normalizedCompanies = Array.isArray(companies) ? companies : [];
  const passwordHash = await hashOfflineSecret(password);
  const cleanupResult = await cleanupOfflineAuthCache({
    userUuid: String(user?.uuid || "").trim(),
    companyUuid: String(user?.companyUuid || "").trim(),
  });
  const currentCache = cleanupResult?.cache && typeof cleanupResult.cache === "object"
    ? cleanupResult.cache
    : await readOfflineAuthCache();
  currentCache[loginKey] = {
    loginKey,
    email: String(email || "").trim().toLowerCase() || null,
    phone: String(phone || "").trim() || null,
    passwordHash,
    user,
    companies: normalizedCompanies,
    boundDeviceId: readOfflineBoundDeviceId() || null,
    subscriptionSnapshot: resolveOfflineSubscriptionSnapshot({ user, companies: normalizedCompanies }),
    session: session ? {
      token: String(session?.token || "").trim(),
      sessionId: session?.sessionId || null,
      sessionUuid: session?.sessionUuid || null,
      cachedAt: Date.now(),
    } : null,
    cachedAt: Date.now(),
  };

  await writeOfflineAuthCache(currentCache);
  clearOfflineLoginAttempts(loginKey);
  writeOfflineCompanies(normalizedCompanies);
}

export async function updateOfflinePasswordCacheForUser({ userUuid, email, phone, newPassword }) {
  const normalizedUserUuid = String(userUuid || "").trim();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedPhoneDigits = String(phone || "").replace(/\D+/g, "").trim();
  const normalizedPassword = String(newPassword || "");

  if (!normalizedPassword || normalizedPassword.length < 6) {
    return { updated: 0 };
  }

  const passwordHash = await hashOfflineSecret(normalizedPassword);
  const currentCache = await readOfflineAuthCache();
  const attemptsCache = readOfflineAttemptsCache();
  let updated = 0;

  Object.keys(currentCache || {}).forEach((loginKey) => {
    const entry = currentCache?.[loginKey];
    if (!entry || typeof entry !== "object") {
      return;
    }

    const entryUserUuid = String(entry?.user?.uuid || "").trim();
    const entryEmail = String(entry?.email || "").trim().toLowerCase();
    const entryPhoneDigits = String(entry?.phone || "").replace(/\D+/g, "").trim();

    const matchesUserUuid = Boolean(normalizedUserUuid && entryUserUuid && entryUserUuid === normalizedUserUuid);
    const matchesEmail = Boolean(normalizedEmail && entryEmail && entryEmail === normalizedEmail);
    const matchesPhone = Boolean(normalizedPhoneDigits && entryPhoneDigits && entryPhoneDigits === normalizedPhoneDigits);

    if (!matchesUserUuid && !matchesEmail && !matchesPhone) {
      return;
    }

    currentCache[loginKey] = {
      ...entry,
      passwordHash,
      cachedAt: Date.now(),
    };

    if (attemptsCache?.[loginKey]) {
      delete attemptsCache[loginKey];
    }

    updated += 1;
  });

  if (updated > 0) {
    await writeOfflineAuthCache(currentCache);
    writeOfflineAttemptsCache(attemptsCache);
  }

  return { updated };
}

export async function updateOfflineCachedUserForUser({ userUuid, email, phone, patchUser = {}, companies = null }) {
  const normalizedUserUuid = String(userUuid || "").trim();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedPhoneDigits = String(phone || "").replace(/\D+/g, "").trim();
  const safePatch = patchUser && typeof patchUser === "object" ? patchUser : {};

  if (!normalizedUserUuid && !normalizedEmail && !normalizedPhoneDigits) {
    return { updated: 0 };
  }

  const currentCache = await readOfflineAuthCache();
  let updated = 0;

  Object.keys(currentCache || {}).forEach((loginKey) => {
    const entry = currentCache?.[loginKey];
    if (!entry || typeof entry !== "object") {
      return;
    }

    const entryUserUuid = String(entry?.user?.uuid || "").trim();
    const entryEmail = String(entry?.email || "").trim().toLowerCase();
    const entryPhoneDigits = String(entry?.phone || "").replace(/\D+/g, "").trim();

    const matchesUserUuid = Boolean(normalizedUserUuid && entryUserUuid && entryUserUuid === normalizedUserUuid);
    const matchesEmail = Boolean(normalizedEmail && entryEmail && entryEmail === normalizedEmail);
    const matchesPhone = Boolean(normalizedPhoneDigits && entryPhoneDigits && entryPhoneDigits === normalizedPhoneDigits);

    if (!matchesUserUuid && !matchesEmail && !matchesPhone) {
      return;
    }

    const mergedUser = {
      ...(entry?.user && typeof entry.user === "object" ? entry.user : {}),
      ...safePatch,
    };

    const nextCompanies = Array.isArray(companies)
      ? companies
      : (Array.isArray(entry?.companies) ? entry.companies : readOfflineCompanies());

    currentCache[loginKey] = {
      ...entry,
      user: mergedUser,
      companies: nextCompanies,
      subscriptionSnapshot: resolveOfflineSubscriptionSnapshot({ user: mergedUser, companies: nextCompanies }),
      cachedAt: Date.now(),
    };

    updated += 1;
  });

  if (updated > 0) {
    await writeOfflineAuthCache(currentCache);
    if (Array.isArray(companies) && companies.length > 0) {
      writeOfflineCompanies(companies);
    }
  }

  return { updated };
}

export async function tryOfflineLogin({ email, phone, password }) {
  const loginKey = resolveLoginCacheKey({ email, phone });
  const normalizedPassword = String(password || "");
  await ensureOfflineStorageReady();
  await cleanupOfflineAuthCache();

  if (!loginKey) {
    return {
      ok: false,
      message: "Informe um email ou telefone válido para autenticação offline.",
    };
  }

  if (!normalizedPassword || normalizedPassword.length < 6) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Senha inválida para login offline.",
    };
  }

  const lockState = resolveOfflineLockState(loginKey);
  if (lockState.isLocked) {
    const remainingMinutes = Math.ceil(lockState.remainingMs / 60000);
    return {
      ok: false,
      message: `Login offline temporariamente bloqueado por tentativas inválidas. Tente novamente em ${remainingMinutes} minuto(s).`,
    };
  }

  const currentCache = await readOfflineAuthCache();
  const cachedRecord = currentCache?.[loginKey];

  if (!cachedRecord) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Este utilizador ainda nao tem cache offline neste dispositivo. Faca pelo menos um login online para ativar o modo offline.",
    };
  }

  const cachedAt = Number(cachedRecord?.cachedAt || 0);
  if (!Number.isFinite(cachedAt) || cachedAt <= 0 || (Date.now() - cachedAt) > OFFLINE_LOGIN_CACHE_MAX_AGE_MS) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Cache offline expirado para este utilizador. Faça login online para renovar as credenciais locais.",
    };
  }

  if (!resolveOfflineUserIsActive(cachedRecord?.user)) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Este utilizador está inativo ou bloqueado e não pode autenticar em modo offline.",
    };
  }

  if (!Array.isArray(cachedRecord?.companies) || cachedRecord.companies.length === 0) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Cache offline incompleto: empresas não encontradas. Faça login online para atualizar os dados.",
    };
  }

  if (!cachedRecord?.passwordHash || String(cachedRecord.passwordHash).length < 32) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Cache offline inválido para este utilizador. Faça login online para recriar o cache.",
    };
  }

  const passwordHash = await hashOfflineSecret(normalizedPassword);
  if (passwordHash !== cachedRecord?.passwordHash) {
    registerOfflineLoginFailure(loginKey);
    return {
      ok: false,
      message: "Senha offline invalida. Se a senha foi alterada recentemente, faca um login online para atualizar o cache offline.",
    };
  }

  clearOfflineLoginAttempts(loginKey);

  return {
    ok: true,
    user: cachedRecord?.user || null,
    companies: cachedRecord?.companies || readOfflineCompanies(),
    token: "offline-session",
    sessionId: null,
    sessionUuid: null,
  };
}

export async function getOfflineLoginRecordForUser(user) {
  const normalizedUserUuid = String(user?.uuid || "").trim();
  const normalizedEmail = String(user?.email || "").trim().toLowerCase();
  const normalizedPhoneDigits = String(user?.phone || user?.telefone || "").replace(/\D+/g, "").trim();

  if (!normalizedUserUuid && !normalizedEmail && !normalizedPhoneDigits) {
    return null;
  }

  const currentCache = await readOfflineAuthCache();
  const entries = Object.values(currentCache || {});

  for (const entry of entries) {
    if (!entry || typeof entry !== "object") {
      continue;
    }

    const entryUserUuid = String(entry?.user?.uuid || "").trim();
    const entryEmail = String(entry?.email || "").trim().toLowerCase();
    const entryPhoneDigits = String(entry?.phone || "").replace(/\D+/g, "").trim();

    if (
      (normalizedUserUuid && entryUserUuid && entryUserUuid === normalizedUserUuid)
      || (normalizedEmail && entryEmail && entryEmail === normalizedEmail)
      || (normalizedPhoneDigits && entryPhoneDigits === normalizedPhoneDigits)
    ) {
      return entry;
    }
  }

  return null;
}

export async function invalidateOfflineLoginCacheForUser(user) {
  const normalizedUserUuid = String(user?.uuid || "").trim();
  const normalizedEmail = String(user?.email || "").trim().toLowerCase();
  const normalizedPhoneDigits = String(user?.phone || user?.telefone || "").replace(/\D+/g, "").trim();
  const currentCache = await readOfflineAuthCache();
  const nextCache = {};
  let removed = 0;

  Object.entries(currentCache || {}).forEach(([loginKey, entry]) => {
    if (!entry || typeof entry !== "object") {
      return;
    }

    const entryUserUuid = String(entry?.user?.uuid || "").trim();
    const entryEmail = String(entry?.email || "").trim().toLowerCase();
    const entryPhoneDigits = String(entry?.phone || "").replace(/\D+/g, "").trim();
    const matches = (
      (normalizedUserUuid && entryUserUuid && entryUserUuid === normalizedUserUuid)
      || (normalizedEmail && entryEmail === normalizedEmail)
      || (normalizedPhoneDigits && entryPhoneDigits === normalizedPhoneDigits)
    );

    if (matches) {
      clearOfflineLoginAttempts(loginKey);
      removed += 1;
      return;
    }

    nextCache[loginKey] = entry;
  });

  if (removed > 0) {
    if (Object.keys(nextCache).length > 0) {
      await writeOfflineAuthCache(nextCache);
    } else {
      await removeOfflineAuthCache();
    }
  }

  return { removed };
}

export function buildCatalogCacheKey({ companyUuid, storeUuid }) {
  return `${OFFLINE_KEYS.catalogPrefix}.${companyUuid || "global"}.${storeUuid || "all"}`;
}

export function buildCustomerCacheKey({ companyUuid, storeUuid }) {
  return `${OFFLINE_KEYS.customerPrefix}.${companyUuid || "global"}.${storeUuid || "all"}`;
}

export function readCatalogSnapshot(scope) {
  return readJson(buildCatalogCacheKey(scope), null);
}

export function writeCatalogSnapshot(scope, snapshot) {
  writeJson(buildCatalogCacheKey(scope), {
    ...(snapshot || {}),
    cachedAt: Date.now(),
  });
}

const CATALOG_SYNC_TYPES = new Set(["saveProduct", "deleteProduct", "updateMinimumStock", "stockMovement"]);

export async function readPersistedCatalogSnapshot(scope) {
  if (!scope?.companyUuid) {
    return null;
  }

  const result = await invokeOfflineCommand("offline_catalog_get_snapshot", {
    companyUuid: scope.companyUuid,
    storeUuid: scope.storeUuid || null,
  });

  if (typeof result === "string") {
    try {
      return JSON.parse(result);
    } catch {
      return null;
    }
  }

  return readCatalogSnapshot(scope);
}

export async function writePersistedCatalogSnapshot(scope, snapshot) {
  if (!scope?.companyUuid) {
    return;
  }

  const normalizedSnapshot = {
    ...(snapshot || {}),
    cachedAt: Date.now(),
  };

  const handled = await invokeOfflineCommand("offline_catalog_set_snapshot", {
    companyUuid: scope.companyUuid,
    storeUuid: scope.storeUuid || null,
    payload: JSON.stringify(normalizedSnapshot),
  });

  if (handled === null) {
    writeCatalogSnapshot(scope, normalizedSnapshot);
  }
}

export async function listPersistedCatalogSyncQueue(companyUuid = null) {
  const result = await invokeOfflineCommand("offline_catalog_list_sync_queue", {
    companyUuid: companyUuid || null,
  });

  if (Array.isArray(result)) {
    return result.map((item) => ({
      ...item,
      type: item?.operationType || item?.type || "saveProduct",
    }));
  }

  return readPendingSyncQueue().filter((item) => CATALOG_SYNC_TYPES.has(item?.type) && (!companyUuid || item?.companyUuid === companyUuid));
}

export async function upsertPersistedCatalogSyncQueueItem(item) {
  const normalizedItem = {
    id: item?.id || `offline-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    type: item?.type || item?.operationType || "saveProduct",
    companyUuid: item?.companyUuid || "",
    storeUuid: item?.storeUuid || null,
    payload: item?.payload || {},
    meta: item?.meta || null,
    status: item?.status || "pending",
    createdAt: item?.createdAt || Date.now(),
  };

  const result = await invokeOfflineCommand("offline_catalog_upsert_sync_queue_item", {
    item: {
      id: normalizedItem.id,
      operationType: normalizedItem.type,
      companyUuid: normalizedItem.companyUuid,
      storeUuid: normalizedItem.storeUuid,
      payload: normalizedItem.payload,
      meta: normalizedItem.meta,
      status: normalizedItem.status,
      createdAt: normalizedItem.createdAt,
    },
  });

  if (result) {
    return {
      ...result,
      type: result?.operationType || normalizedItem.type,
    };
  }

  const currentQueue = readPendingSyncQueue();
  const queueIndex = currentQueue.findIndex((queueItem) => queueItem?.id === normalizedItem.id);
  if (queueIndex >= 0) {
    currentQueue[queueIndex] = normalizedItem;
  } else {
    currentQueue.push(normalizedItem);
  }
  writePendingSyncQueue(currentQueue);
  return normalizedItem;
}

export async function removePersistedCatalogSyncQueueItem(itemId) {
  const handled = await invokeOfflineCommand("offline_catalog_remove_sync_queue_item", {
    itemId,
  });

  if (handled === null) {
    removePendingSyncItem(itemId);
  }
}

export function readCustomerSnapshot(scope) {
  return readJson(buildCustomerCacheKey(scope), null);
}

export function writeCustomerSnapshot(scope, snapshot) {
  writeJson(buildCustomerCacheKey(scope), {
    ...(snapshot || {}),
    cachedAt: Date.now(),
  });
}

export async function readPersistedCustomerSnapshot(scope) {
  if (!scope?.companyUuid) {
    return null;
  }

  const result = await invokeOfflineCommand("offline_customers_get_snapshot", {
    companyUuid: scope.companyUuid,
    storeUuid: scope.storeUuid || null,
  });

  if (typeof result === "string") {
    try {
      return JSON.parse(result);
    } catch {
      return null;
    }
  }

  return readCustomerSnapshot(scope);
}

export async function writePersistedCustomerSnapshot(scope, snapshot) {
  if (!scope?.companyUuid) {
    return;
  }

  const normalizedSnapshot = {
    ...(snapshot || {}),
    cachedAt: Date.now(),
  };

  const handled = await invokeOfflineCommand("offline_customers_set_snapshot", {
    companyUuid: scope.companyUuid,
    storeUuid: scope.storeUuid || null,
    payload: JSON.stringify(normalizedSnapshot),
  });

  if (handled === null) {
    writeCustomerSnapshot(scope, normalizedSnapshot);
  }
}

export async function listPersistedCustomerSyncQueue(companyUuid = null) {
  const result = await invokeOfflineCommand("offline_customers_list_sync_queue", {
    companyUuid: companyUuid || null,
  });

  if (Array.isArray(result)) {
    return result.map((item) => ({
      ...item,
      type: "saveCustomer",
    }));
  }

  return readPendingSyncQueue().filter((item) => item?.type === "saveCustomer" && (!companyUuid || item?.companyUuid === companyUuid));
}

export async function upsertPersistedCustomerSyncQueueItem(item) {
  const normalizedItem = {
    id: item?.id || `offline-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    companyUuid: item?.companyUuid || "",
    storeUuid: item?.storeUuid || null,
    payload: item?.payload || {},
    meta: item?.meta || null,
    status: item?.status || "pending",
    createdAt: item?.createdAt || Date.now(),
  };

  const result = await invokeOfflineCommand("offline_customers_upsert_sync_queue_item", {
    item: normalizedItem,
  });

  if (result) {
    return {
      ...result,
      type: "saveCustomer",
    };
  }

  const currentQueue = readPendingSyncQueue();
  const queueIndex = currentQueue.findIndex((queueItem) => queueItem?.id === normalizedItem.id);
  if (queueIndex >= 0) {
    currentQueue[queueIndex] = {
      ...currentQueue[queueIndex],
      ...normalizedItem,
      type: "saveCustomer",
    };
  } else {
    currentQueue.push({
      ...normalizedItem,
      type: "saveCustomer",
    });
  }
  writePendingSyncQueue(currentQueue);
  return {
    ...normalizedItem,
    type: "saveCustomer",
  };
}

export async function removePersistedCustomerSyncQueueItem(itemId) {
  const handled = await invokeOfflineCommand("offline_customers_remove_sync_queue_item", {
    itemId,
  });

  if (handled === null) {
    removePendingSyncItem(itemId);
  }
}

export function readPendingSyncQueue() {
  return readJson(OFFLINE_KEYS.syncQueue, []);
}

export function writePendingSyncQueue(items) {
  writeJson(OFFLINE_KEYS.syncQueue, Array.isArray(items) ? items : []);
}

export function enqueuePendingSync(item) {
  const currentQueue = readPendingSyncQueue();
  const nextItem = {
    id: item?.id || `offline-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    createdAt: item?.createdAt || Date.now(),
    ...item,
  };

  currentQueue.push(nextItem);
  writePendingSyncQueue(currentQueue);
  return nextItem;
}

export function removePendingSyncItem(itemId) {
  const currentQueue = readPendingSyncQueue();
  const nextQueue = currentQueue.filter((item) => item?.id !== itemId);
  writePendingSyncQueue(nextQueue);
}

export function clearPendingSyncQueue() {
  deleteJson(OFFLINE_KEYS.syncQueue);
}

if (canUseWindow()) {
  void ensureOfflineStorageReady();
}

export async function persistOfflineOperationalContext(value) {
  await writePersistedJson(OFFLINE_OPERATIONAL_CONTEXT_KEY, {
    ...(value && typeof value === "object" ? value : {}),
    updatedAt: Date.now(),
  });
}

export function readOfflineOperationalContext() {
  return readPersistedJson(OFFLINE_OPERATIONAL_CONTEXT_KEY, null);
}

export async function clearOfflineOperationalContext() {
  await removePersistedJson(OFFLINE_OPERATIONAL_CONTEXT_KEY);
}